import type { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useDraftMode } from "../contexts/DraftModeContext";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { trackEvent } from "../utils/analytics";
import { type BaseNurseShift, type HospitalData, type NurseInShift, NurseTrainingStatus } from "../utils/models";
import { useHandleNurseChange } from "./useHandleNurseChange";

/** The training statuses a shift can be flagged with. They are mutually exclusive; `Regular` clears them. */
export type ShiftTrainingFlag = NurseTrainingStatus.Trainee | NurseTrainingStatus.Orientation | NurseTrainingStatus.Education;

/**
 * The shift to flag and where it sits in `hospitalData` — every staffing mutation is keyed by these
 * coordinates. A caller holding only a `NurseInShift` resolves them the way `NormalizedNurseMenu` does
 * (`getWeekIndexByDateTime` / `getShiftIndexByShiftTimes` / `getDayIndexByDateTime` on its times).
 */
export type ShiftFlagTarget = {
	nurseInShift: NurseInShift;
	weekIndex: number;
	shiftNo: number;
	dayNo: number;
	/** Passed through to the mutations untouched: they treat `undefined` as the scheduled view. */
	showActual: boolean | undefined;
	/** Surface name sent as the events' `source`, e.g. `"nurse_menu"`. */
	analyticsSource: string;
};

type NurseChange = ReturnType<typeof useHandleNurseChange>;

// The actual view's training events carry this suffix on their source; the charge event never did.
const ACTUAL_VIEW_SOURCE_SUFFIX = "_actual";
const ANALYTICS_DATE_FORMAT = "MM/DD/yyyy";

const flagEventProperties = ({
	source,
	nurseId,
	shiftNo,
	dayNo,
	date,
}: {
	source: string;
	nurseId: number;
	shiftNo: number;
	dayNo: number;
	date: DateTime;
}) => ({
	source,
	nurse_id: nurseId,
	shift_no: shiftNo,
	day_no: dayNo,
	date: date.toFormat(ANALYTICS_DATE_FORMAT),
});

// The Mixpanel event for a training-status change: the NEW status is checked first, then the current
// one. The trainee names read inverted, but they are the shipped event names — kept verbatim so the
// event history stays continuous.
const trainingStatusEventName = (current: NurseTrainingStatus, next: NurseTrainingStatus): string => {
	if (next === NurseTrainingStatus.Education) return "Staffing - mark education";
	if (current === NurseTrainingStatus.Education) return "Staffing - remove education";
	if (current === NurseTrainingStatus.Trainee) return "Staffing - mark trainee";
	return "Staffing - remove trainee";
};

// A training status belongs to the nurse's whole day, so it is written to every shift she has that
// day across all shift types, scheduled and actual alike.
const collectNurseDayShifts = (hospitalData: HospitalData, nurseInShift: NurseInShift, weekIndex: number, dayNo: number, showActual: boolean | undefined) => {
	const { weeklyShifts } = hospitalData.weeks[weekIndex];
	const foundActual = weeklyShifts.flatMap((shift) => {
		return shift.days[dayNo].nursesActual?.filter((nurse): nurse is NurseInShift => nurse !== undefined && nurse.nurseId === nurseInShift.nurseId) || [];
	});
	// When in actual view and nursesActual lookup misses (e.g., unscheduled actual),
	// use nurseInShift directly — it IS the actual shift.
	const shiftsActual: NurseInShift[] = showActual && foundActual.length === 0 ? [nurseInShift] : foundActual;
	const shifts: NurseInShift[] = weeklyShifts.flatMap((shift) => shift.days[dayNo].nurses.filter((nurse) => nurse.nurseId === nurseInShift.nurseId));
	return { shifts, shiftsActual };
};

const applyTrainingStatus = (
	prevData: HospitalData,
	{ weekIndex, dayNo, nurseId }: { weekIndex: number; dayNo: number; nurseId: number },
	trainingStatus: NurseTrainingStatus,
	isDraftMode: boolean,
): HospitalData => {
	const newData = { ...prevData };
	const withStatus = <T extends BaseNurseShift>(nurse: T): T => ({ ...nurse, trainingStatus, ...(isDraftMode ? { isPendingToUpdate: true } : {}) });

	newData.weeks[weekIndex].weeklyShifts.forEach((shift) => {
		shift.days[dayNo].nurses = shift.days[dayNo].nurses.map((nurse) => (nurse.nurseId === nurseId ? withStatus(nurse) : nurse));

		if (shift.days[dayNo].nursesActual) {
			shift.days[dayNo].nursesActual = shift.days[dayNo].nursesActual.map((nurse) => (nurse && nurse.nurseId === nurseId ? withStatus(nurse) : nurse));
		}
	});

	return newData;
};

const useChargeToggle = (
	{ nurseInShift, weekIndex, shiftNo, dayNo, showActual, analyticsSource }: ShiftFlagTarget,
	toggleNurseCharge: NurseChange["toggleNurseCharge"],
	isSaving: boolean,
) => {
	const { hospitalData, getShiftDate, updateNurseChargeStatus, showErrorSnackbar } = useHospitalData();
	const { isDraftMode } = useDraftMode();
	// DEFAULT: carried over from NurseMenu — a shift outside the loaded weeks has no type, and the charge
	// write has always sent "" for it rather than refusing.
	const shiftType = useMemo(() => hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.type || "", [hospitalData?.weeks, weekIndex, shiftNo]);

	return useCallback(async (): Promise<boolean> => {
		if (isSaving) return false;

		const date = getShiftDate(weekIndex, shiftNo, dayNo);
		const properties = flagEventProperties({ source: analyticsSource, nurseId: nurseInShift.nurseId, shiftNo, dayNo, date });
		trackEvent(nurseInShift.charge ? "Staffing - remove charge" : "Staffing - make charge", properties);

		try {
			await toggleNurseCharge({ nurse: { ...nurseInShift, shiftType }, shiftIndex: shiftNo, dayNo, weekIndex, isActual: showActual });
		} catch (e) {
			showErrorSnackbar("Failed to update nurse charge", { nurseId: nurseInShift.id }, e);
			return false;
		}

		updateNurseChargeStatus(nurseInShift.id, weekIndex, shiftNo, dayNo, !nurseInShift.charge, isDraftMode);
		return true;
	}, [
		isSaving,
		getShiftDate,
		weekIndex,
		shiftNo,
		dayNo,
		analyticsSource,
		nurseInShift,
		toggleNurseCharge,
		shiftType,
		showActual,
		showErrorSnackbar,
		updateNurseChargeStatus,
		isDraftMode,
	]);
};

const useTrainingStatusSetter = (
	{ nurseInShift, weekIndex, shiftNo, dayNo, showActual, analyticsSource }: ShiftFlagTarget,
	setNurseTrainingStatus: NurseChange["setNurseTrainingStatus"],
	isSaving: boolean,
) => {
	const { hospitalData, setHospitalData, getShiftDate, showErrorSnackbar } = useHospitalData();
	const { isDraftMode } = useDraftMode();

	return useCallback(
		async (newTrainingStatus: NurseTrainingStatus): Promise<boolean> => {
			if (isSaving) return false;

			const source = showActual ? `${analyticsSource}${ACTUAL_VIEW_SOURCE_SUFFIX}` : analyticsSource;
			const date = getShiftDate(weekIndex, shiftNo, dayNo);
			const properties = flagEventProperties({ source, nurseId: nurseInShift.nurseId, shiftNo, dayNo, date });
			trackEvent(trainingStatusEventName(nurseInShift.trainingStatus, newTrainingStatus), { ...properties, training_status: newTrainingStatus });

			try {
				const { shifts, shiftsActual } = collectNurseDayShifts(hospitalData, nurseInShift, weekIndex, dayNo, showActual);
				await setNurseTrainingStatus({ shifts, shiftsActual, shiftIndex: shiftNo, dayNo, weekIndex, trainingStatus: newTrainingStatus });
				setHospitalData((prevData) => applyTrainingStatus(prevData, { weekIndex, dayNo, nurseId: nurseInShift.nurseId }, newTrainingStatus, isDraftMode));
			} catch (e) {
				showErrorSnackbar("Failed to update nurse training status", { nurseId: nurseInShift.id }, e);
				return false;
			}
			return true;
		},
		[
			isSaving,
			showActual,
			analyticsSource,
			getShiftDate,
			weekIndex,
			shiftNo,
			dayNo,
			nurseInShift,
			hospitalData,
			setNurseTrainingStatus,
			setHospitalData,
			isDraftMode,
			showErrorSnackbar,
		],
	);
};

/**
 * Charge and training-status (Trainee / Orientation / Education) toggles for one shift: the write, the
 * local `hospitalData` update and the analytics, shared by the desktop nurse menu and the mobile
 * staff-row sheet. Each action resolves `true` once the change is written, `false` when it was
 * skipped (a write already in flight) or failed — a failure is already shown in the error snackbar.
 */
export const useShiftFlagActions = (target: ShiftFlagTarget) => {
	const { isLoading, toggleNurseCharge, setNurseTrainingStatus } = useHandleNurseChange();
	const toggleCharge = useChargeToggle(target, toggleNurseCharge, isLoading.toggleNurseCharge);
	const setTrainingStatus = useTrainingStatusSetter(target, setNurseTrainingStatus, isLoading.setNurseTrainingStatus);
	const currentTrainingStatus = target.nurseInShift.trainingStatus;

	/** Sets `flag`, replacing any other training status — or clears it back to Regular when it is already set. */
	const toggleTrainingStatus = useCallback(
		(flag: ShiftTrainingFlag) => setTrainingStatus(currentTrainingStatus === flag ? NurseTrainingStatus.Regular : flag),
		[currentTrainingStatus, setTrainingStatus],
	);

	return {
		toggleCharge,
		toggleTrainingStatus,
		isChargeSaving: isLoading.toggleNurseCharge,
		isTrainingStatusSaving: isLoading.setNurseTrainingStatus,
	};
};
